import { RequestStatus } from '@/types/sso';
import { capitalize, capitalizeAll } from '@/utils/format';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { PortalRecentTransactionsRow } from '@/utils/hooks/usePortalRecentTransactionsTable';
import { CellContext } from '@tanstack/react-table';
import { MdErrorOutline, MdOutlineCheckCircleOutline, MdOutlineQueryBuilder } from 'react-icons/md';
import { generalAmountCell } from './currencyHelper';

interface PortalRecentTransactionsColumnsParams {
    lang: UseLanguageReturn;
    mode: 'bank' | 'crypto';
    formatQty: Intl.NumberFormat;
    formatDate: Intl.DateTimeFormat;
}

export const portalRecentTransactionsColumns = (params: PortalRecentTransactionsColumnsParams) => {
    const { lang, mode, formatQty, formatDate } = params;

    const columns = [
        {
            accessorKey: 'updatedAt',
            header: lang.blotters.date,
            cell: ({ row, getValue }: CellContext<PortalRecentTransactionsRow, any>) => {
                const value = getValue();
                const date = new Date(value);

                return <div className="">{formatDate.format(date)}</div>;
            }
        },
        {
            accessorKey: 'requestType',
            header: lang.blotters.type,
            cell: ({ row, getValue }: CellContext<PortalRecentTransactionsRow, any>) => {
                const requestType = getValue();
                return <div className="">{capitalizeAll(requestType)}</div>;
            }
        },
        {
            accessorKey: 'account',
            header: lang.blotters.bankAccount,
            cell: ({ row, getValue }: CellContext<PortalRecentTransactionsRow, any>) => {
                const request = row.original as any;

                // const maskedAccountNumber = useMemo(() => {
                //     if (request.details.accountNumber) return request.details.accountNumber.replace(/.(?=.{4})/g, '*');
                //     else if (request.details.ibanCode) return request.details.ibanCode.replace(/.(?=.{4})/g, '*');
                //     return 'N/A';
                // }, [request.details]);

                return (
                    <div className="">
                        {request.details.bankFriendlyName || request.details.bankInfoFriendlyName || '-'}
                        {/* {request.details.bankName} {maskedAccountNumber} */}
                    </div>
                );
            }
        },
        {
            accessorKey: 'address',
            header: lang.blotters.wallet,
            cell: ({ row }: CellContext<PortalRecentTransactionsRow, any>) => {
                const request = row.original as any;

                return <div className="">{request.details.walletFriendlyName || '-'}</div>;
            }
        },
        {
            accessorKey: 'details.amount',
            header: lang.blotters.amount,
            cell: ({ row }: CellContext<PortalRecentTransactionsRow, any>) => {
                const amount = row.original.details.amount;
                const ccy = row.original.details.currencyName;

                return generalAmountCell({
                    amount,
                    ccy,
                    formatQty
                });
            }
        },
        {
            accessorKey: 'requestStatus',
            header: lang.blotters.status,
            cell: ({ row, getValue }: CellContext<PortalRecentTransactionsRow, RequestStatus>) => {
                const value = getValue();

                if (value === 'INITIATED') {
                    return (
                        <div className="text-yellow-400 capitalize flex items-center gap-1">
                            <MdOutlineQueryBuilder className="h-4 w-4" />
                            Processing
                        </div>
                    );
                } else if (value === 'APPROVED') {
                    return (
                        <div className="text-brand-green capitalize flex items-center gap-1">
                            <MdOutlineCheckCircleOutline className="h-4 w-4" />
                            Completed
                        </div>
                    );
                } else if (['CANCELED', 'REJECTED'].includes(value)) {
                    return (
                        <div className="text-brand-red capitalize flex items-center gap-1">
                            <MdErrorOutline className="h-4 w-4" />
                            {capitalize(value)}
                        </div>
                    );
                }
                return <div className=" capitalize flex items-center gap-1">{value}</div>;
            }
        }
    ];

    if (mode === 'bank') {
        return columns.filter((column) => column.accessorKey !== 'address');
    } else {
        return columns.filter((column) => column.accessorKey !== 'account');
    }
};
